import { Box, Grid, Typography } from "@mui/material";
import {
  FlexVerticalAligned,
  Heading1,
} from "../../common/components/styled/styled";
import TalentStatusIcon, {
  StatusTextStyle,
} from "../../common/components/functional/talentStatus/TalentStatusIcon";
import TalentSkillSet from "../talentProfile/talentProfileSummary/talentSkillSet/TalentSkillSet";
import TalentWorkHistory from "../workHistory/TalentWorkHistory";
import { useTranslation } from "react-i18next";
import History from "../talentProfile/comments/History";
import TalentHrEezyReview from "../talentProfile/talentHrRepresentative/TalentHrEezyReview";
import { CustomButton } from "../../common/components/functional/customButton/CustomButton";
import { useNavigate } from "react-router-dom";
import { routes } from "../../app/paths";
import { ReactComponent as ContinueIcon } from "../../common/icons/arrows-right-filled.svg";
import IconWrapper from "../../common/components/functional/icons/IconWrapper";
import { Talent, TalentPreview } from "./talentAPI";
import { PopperActionBannerContainer } from "../../common/components/functional/hoverPopper/styled";
import isEmpty from "../../common/helpers/isEmpty";
import { PreviewSectionHeader } from "./talentStyled";
import OverlaySpinner from "../../common/components/functional/overlaySpinner/OverlaySpinner";
import TalentBasicInfo from "../talentProfile/talentProfileSummary/talentBasicInfo/TalentBasicInfo";
import TalentIntroducedToCustomer from "../talentProfile/talentProfileSummary/talentSkillSet/talentIntroducedToCustomer/TalentIntroducedToCustomer";

type Props = {
  talentId: string;
  talentPreview: TalentPreview | undefined;
  isLoading: boolean;
};

export const TalentPopperContent = ({
  talentId,
  talentPreview,
  isLoading,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <>
      <OverlaySpinner isLoading={isLoading} />
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          bgcolor: (theme) => theme.palette.background.default,
          p: "8px 16px 16px 16px",
          width: "1054px",
        }}
      >
        <FlexVerticalAligned justifyContent="space-between" width="100%">
          <Heading1
            visibility={talentPreview?.talent.account ? "visible" : "hidden"}
          >{`${talentPreview?.talent.account.firstName} ${talentPreview?.talent.account.lastName}`}</Heading1>
          <TalentStatusIcon
            status={talentPreview?.talent.account?.status}
            statusChanged={talentPreview?.talent.account.statusChanged}
            statusTextStyle={StatusTextStyle.LONG}
          />
        </FlexVerticalAligned>
        <Grid item container spacing={1}>
          <Grid item xs={6}>
            {talentPreview && (
              <TalentBasicInfo
                talent={{ ...talentPreview?.talent, id: talentId } as Talent}
                previewMode
              />
            )}
            <TalentSkillSet talent={talentPreview?.talent as Talent} />
          </Grid>
          <Grid item xs={6}>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
              }}
            >
              <PreviewSectionHeader variant="header">
                {t("workHistory:workExperience")}
              </PreviewSectionHeader>
              <TalentWorkHistory
                workHistories={talentPreview?.workHistory || []}
                handleOpenEditModal={() => {}}
                isPreviewMode
              />
            </Box>
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "12px",
                marginBottom: "8px",
              }}
            >
              <PreviewSectionHeader variant="header">
                {t("talentProfile:newestComments")}
              </PreviewSectionHeader>
              {!isEmpty(talentPreview?.comments) ? (
                <History
                  previewMode
                  comments={talentPreview?.comments || []}
                  currentUserId={null}
                />
              ) : (
                <Typography variant="secondary" p="12px">
                  {t("talentProfile:noComments")}
                </Typography>
              )}
            </Box>
            {talentPreview && (
              <TalentIntroducedToCustomer
                talent={{ ...talentPreview.talent, id: talentId } as Talent}
                previewMode
                introductionsWithoutOrder={
                  talentPreview.introductionsWithoutOrder
                }
                introductionsWithOrder={talentPreview.introductionsWithOrder}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            <TalentHrEezyReview
              talent={talentPreview?.talent as Talent}
              disableEdit
            />
          </Grid>
        </Grid>
      </Box>
      <PopperActionBannerContainer>
        <CustomButton
          variant="contained"
          sx={{ borderRadius: "20px", height: "42px" }}
          onClick={() => navigate(routes.talentProfile(talentId))}
          endIcon={
            <IconWrapper
              icon={ContinueIcon}
              sx={{
                "& path": { fill: "#fff" },
              }}
            />
          }
        >
          {t("talentProfile:showWholeProfile")}
        </CustomButton>
      </PopperActionBannerContainer>
    </>
  );
};
